/**
 * Formatting core — every number, date, time and money value in the app goes through here.
 *
 * ## Why this module exists
 *
 * Before it, `src/` contained **130 bare `toLocaleString()` calls**, most of them on money. A bare
 * call follows the *browser's* locale, not the app's, so switching the UI to Arabic left salary
 * figures formatting by whatever the visitor's browser happened to say.
 *
 * It also fixes a defect that predates any Arabic work: `currencyUtils` maps SAR/OMR/QAR/KWD/BHD
 * to `ar-SA`/`ar-OM`/`ar-QA`/`ar-KW`/`ar-BH`, and those tags default to the `arab` numbering
 * system — so an org on any Gulf currency other than AED has been rendering money in Arabic-Indic
 * digits (`١٬٢٣٤`) inside an otherwise English UI. Every formatter here pins `nu-latn`.
 *
 * ## Deliberately not a hook
 *
 * Most of the call sites needing it are antd column `render` callbacks, utility modules and
 * service code — none of which can call a hook. So the active locale is module state, set once by
 * `LocaleProvider`. React components re-render on locale change through the provider's context;
 * these functions simply read the current value.
 *
 * Design: docs/43-I18N-ARABIC-DESIGN.md §6.
 */

import { DEFAULT_LOCALE, LOCALES, type LocaleCode } from './locales';
import { getGlobalCurrencyLocale, getGlobalCurrencySymbol } from '../utils/currencyUtils';

// ── Active locale ────────────────────────────────────────────────────────────

let activeLocale: LocaleCode = DEFAULT_LOCALE;
let hijriEnabled = false;

/** Called by LocaleProvider. Not for general use — change the locale through the provider. */
export const setActiveFormattingLocale = (locale: LocaleCode): void => {
  activeLocale = locale;
};

export const getActiveFormattingLocale = (): LocaleCode => activeLocale;

/** Secondary Hijri display, driven by the org's `ORG_SHOW_HIJRI_DATES` setting. */
export const setHijriDisplay = (enabled: boolean): void => {
  hijriEnabled = enabled;
};

export const isHijriDisplayEnabled = (): boolean => hijriEnabled;

// ── Locale resolution for Intl ───────────────────────────────────────────────

/**
 * Force the Latin numbering system onto any tag.
 *
 * `Intl` accepts at most one `-u-` section, so a tag that already carries extensions must have
 * `nu-latn` merged into it rather than appended — appending a second `-u-` silently produces an
 * invalid tag and throws `RangeError`.
 */
const pinLatinDigits = (tag: string): string => {
  if (/-u-.*\bnu-/.test(tag)) return tag;
  if (tag.includes('-u-')) return tag.replace('-u-', '-u-nu-latn-');
  return `${tag}-u-nu-latn`;
};

/**
 * The tag used for numbers and money.
 *
 * Arabic uses its own pinned tag. English keeps the *currency's* locale (so PKR keeps `en-PK`
 * grouping and INR keeps its lakh/crore grouping), but with Latin digits forced — which is the
 * ar-SA fix described in the module header.
 */
const numericLocale = (): string =>
  activeLocale === DEFAULT_LOCALE
    ? pinLatinDigits(getGlobalCurrencyLocale())
    : LOCALES[activeLocale].intlLocale;

/** The tag used for dates and times. Always the UI language — dates are prose, not money. */
const temporalLocale = (): string => pinLatinDigits(LOCALES[activeLocale].intlLocale);

// ── Cached formatters ────────────────────────────────────────────────────────
// Intl constructors are expensive and these run inside table render callbacks, thousands of times
// per page. Keyed by locale + options so a locale switch simply misses the cache.

const numberFormatters = new Map<string, Intl.NumberFormat>();
const dateFormatters = new Map<string, Intl.DateTimeFormat>();

const numberFormatter = (locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat => {
  const key = `${locale}|${JSON.stringify(options)}`;
  let f = numberFormatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale, options);
    numberFormatters.set(key, f);
  }
  return f;
};

const dateFormatter = (locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  const key = `${locale}|${JSON.stringify(options)}`;
  let f = dateFormatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, options);
    dateFormatters.set(key, f);
  }
  return f;
};

/** Test seam — the caches outlive a locale change by design, but tests need a clean slate. */
export const __resetFormatterCaches = (): void => {
  numberFormatters.clear();
  dateFormatters.clear();
};

// ── Bidi isolation ───────────────────────────────────────────────────────────

/**
 * Unicode isolate marks. A money token, a date range or a `+971` phone number dropped into an
 * Arabic sentence gets its surrounding neutrals (spaces, punctuation, digits) absorbed by the
 * wrong direction run and renders in visually scrambled order.
 *
 * `FIRST STRONG ISOLATE` + `POP DIRECTIONAL ISOLATE` bind the token so it renders atomically
 * wherever it lands. These are formatting characters — invisible, and stripped by `.trim()`-style
 * comparisons only if a caller compares raw strings, which is why isolation is applied at the
 * display edge and never to a value that gets compared or exported.
 */
const FSI = '⁨';
const PDI = '⁩';

/** Wrap a token so it renders atomically inside RTL text. No-op in LTR. */
export const isolate = (text: string): string =>
  activeLocale === DEFAULT_LOCALE ? text : `${FSI}${text}${PDI}`;

// ── Numbers ──────────────────────────────────────────────────────────────────

const toNumber = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) ? n : null;
};

/** The placeholder for a missing value. One character, same in both directions. */
export const EMPTY_VALUE = '—';

export const formatNumber = (
  value: number | string | null | undefined,
  options: Intl.NumberFormatOptions = { maximumFractionDigits: 2 },
): string => {
  const n = toNumber(value);
  if (n === null) return EMPTY_VALUE;
  return numberFormatter(numericLocale(), options).format(n);
};

export const formatPercent = (
  value: number | string | null | undefined,
  fractionDigits = 1,
): string => {
  const n = toNumber(value);
  if (n === null) return EMPTY_VALUE;
  // Values arrive as percentages already (85 meaning 85%), not as ratios — matching how every
  // existing call site stores them.
  return isolate(
    `${numberFormatter(numericLocale(), {
      minimumFractionDigits: 0,
      maximumFractionDigits: fractionDigits,
    }).format(n)}%`,
  );
};

// ── Money ────────────────────────────────────────────────────────────────────

/**
 * Money, isolated so the symbol never detaches from its number in RTL.
 *
 * The symbol comes from `currencyUtils`, which resolves the org's country package — including its
 * deliberate "AED as text until Unicode 18.0 font support actually lands" behaviour. Arabic never
 * substitutes `إ.د`; see docs/44-I18N-UAE-ARABIC-GLOSSARY.md rule 7.
 *
 * The symbol leads the number in both directions. In RTL the isolate makes the whole token a
 * single neutral run, so it reads correctly whichever side of an Arabic phrase it lands on.
 */
export const formatMoney = (
  value: number | string | null | undefined,
  options: Intl.NumberFormatOptions = { minimumFractionDigits: 0, maximumFractionDigits: 2 },
): string => {
  const n = toNumber(value);
  const symbol = getGlobalCurrencySymbol();
  if (n === null) return isolate(`${symbol} 0`);
  return isolate(`${symbol} ${numberFormatter(numericLocale(), options).format(n)}`);
};

/** Compact money for KPI tiles — `AED 1.2M`. Suffixes are translated by the caller's namespace. */
export const formatCompactMoney = (
  value: number | string | null | undefined,
  suffixes: { billion: string; million: string; thousand: string } = {
    billion: 'B',
    million: 'M',
    thousand: 'K',
  },
): string => {
  const n = toNumber(value);
  if (n === null) return formatMoney(0);
  const symbol = getGlobalCurrencySymbol();
  const abs = Math.abs(n);
  const compact = (divisor: number, suffix: string): string =>
    `${symbol} ${numberFormatter(numericLocale(), { maximumFractionDigits: 1 }).format(n / divisor)}${suffix}`;

  if (abs >= 1_000_000_000) return isolate(compact(1_000_000_000, suffixes.billion));
  if (abs >= 1_000_000) return isolate(compact(1_000_000, suffixes.million));
  if (abs >= 1_000) return isolate(compact(1_000, suffixes.thousand));
  return formatMoney(n);
};

// ── Dates and times ──────────────────────────────────────────────────────────

const toDate = (value: Date | string | number | null | undefined): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Read a naive timestamp as UTC.
 *
 * The .NET services return `DateTime.UtcNow` values with no timezone designator - "2026-09-04
 * T13:28:37.637" - and JavaScript parses a string in that shape as LOCAL time. On a UTC+4 or
 * UTC+5 browser every one of those reads hours early: a step completed a second ago showed as
 * "5 hours ago" on the case file.
 *
 * Applied at the call sites that receive fields the API itself names `...Utc`, deliberately not
 * inside `toDate` for everything: some values in this estate genuinely are local-naive (an
 * attendance punch, for one), and shifting those the other way would be a worse bug than the one
 * being fixed. A value that already carries an offset or a Z is left exactly as it is.
 */
export const asUtc = (value: string | null | undefined): string | null | undefined => {
  if (!value || typeof value !== 'string') return value;
  const naiveDateTime = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
  return naiveDateTime.test(value.trim()) ? `${value.trim().replace(' ', 'T')}Z` : value;
};

export const formatDate = (
  value: Date | string | number | null | undefined,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string => {
  const d = toDate(value);
  if (!d) return EMPTY_VALUE;
  return isolate(dateFormatter(temporalLocale(), options).format(d));
};

export const formatTime = (
  value: Date | string | number | null | undefined,
  options: Intl.DateTimeFormatOptions = { timeStyle: 'short' },
): string => {
  const d = toDate(value);
  if (!d) return EMPTY_VALUE;
  return isolate(dateFormatter(temporalLocale(), options).format(d));
};

export const formatDateTime = (
  value: Date | string | number | null | undefined,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string => {
  const d = toDate(value);
  if (!d) return EMPTY_VALUE;
  return isolate(dateFormatter(temporalLocale(), options).format(d));
};

/**
 * A date range as one isolated token.
 *
 * `formatRange` is used rather than joining two formatted dates with a dash, because the dash is a
 * bidi-neutral character: joining by hand puts the later date on the left in RTL, which reads as
 * the range running backwards.
 */
export const formatDateRange = (
  from: Date | string | number | null | undefined,
  to: Date | string | number | null | undefined,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string => {
  const a = toDate(from);
  const b = toDate(to);
  if (!a && !b) return EMPTY_VALUE;
  if (!a || !b) return formatDate(a ?? b, options);
  return isolate(dateFormatter(temporalLocale(), options).formatRange(a, b));
};

/**
 * Secondary Hijri rendering, opt-in per org via `ORG_SHOW_HIJRI_DATES`.
 *
 * **Display only — never feeds a calculation.** Leave accrual, payroll periods, service dates and
 * gratuity all stay Gregorian. Umm al-Qura is the civil calendar used by the UAE and Saudi Arabia.
 * Returns null when the org has not enabled it, so callers render nothing rather than a fallback.
 */
export const formatHijriDate = (
  value: Date | string | number | null | undefined,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string | null => {
  if (!hijriEnabled) return null;
  const d = toDate(value);
  if (!d) return null;
  const base = LOCALES[activeLocale].code === 'en' ? 'en' : 'ar-AE';
  try {
    return isolate(
      dateFormatter(`${base}-u-ca-islamic-umalqura-nu-latn`, options).format(d),
    );
  } catch {
    // An engine without the Umm al-Qura calendar must not take a screen down over a secondary
    // date. Older Safari and some embedded runtimes lack it.
    return null;
  }
};

/**
 * Hours and minutes, for attendance and overtime.
 *
 * Takes minutes because that is what the attendance endpoints return. Units are passed in by the
 * caller from its own namespace, so this module holds no translatable text.
 */
export const formatDuration = (
  totalMinutes: number | string | null | undefined,
  units: { hour: string; minute: string } = { hour: 'h', minute: 'm' },
): string => {
  const n = toNumber(totalMinutes);
  if (n === null) return EMPTY_VALUE;
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(Math.round(n));
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  const fmt = (v: number) => numberFormatter(numericLocale(), { maximumFractionDigits: 0 }).format(v);
  if (hours === 0) return isolate(`${sign}${fmt(minutes)}${units.minute}`);
  if (minutes === 0) return isolate(`${sign}${fmt(hours)}${units.hour}`);
  return isolate(`${sign}${fmt(hours)}${units.hour} ${fmt(minutes)}${units.minute}`);
};
